import React, { PureComponent } from 'react';
import { Redirect } from 'react-router-dom';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Div } from 'Common/components/Atoms/Div';
import { Flex } from 'Common/components/Atoms/Flex';
import { PageContainer } from 'Common/components/Templates/PageContainer';
import { Text } from 'Common/components/Atoms/Text';
import SignupStepper from '../../components/SignupStepper';
import ReviewerProfileTerms from '../ReviewerProfileTerms';
import ReviewerProfileLinks from '../ReviewerProfileLinks';
import AdditionalInfo from '../ReviewerOnboarding/Application/AdditionalInfo';
import ReviewerProfileSkills from '../ReviewerProfileSkills';
import ReviewerProfileExpertise from '../ReviewerProfileExpertise';
import { actions as signupsActions } from '../../reducers/signups';
import { actions as accountActions } from '../../reducers/Account';
import { accountSelector, isLoadingAccountSelector } from 'selectors/Account';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { User } from '../../models/User';

export const InputLabel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Text variant="h4" mb={1}>
      {children}
    </Text>
  );
};

export interface Props {
  isUpdating: boolean;
  error?: string;
  history: {
    push: (url: string) => void;
  };
  user: User | null;
  isLoadingUser: boolean;
}

interface State {
  step: number;
}

export class ReviewerSignUpProfile extends PureComponent<Props, State> {
  state: State = {
    step: 0,
  };

  componentDidUpdate(prevProps: Props) {
    const { isUpdating, error, history, user, isLoadingUser } = this.props;
    if (
      prevProps.isUpdating &&
      !isUpdating &&
      !error &&
      prevProps.user?.terms_accepted
    ) {
      this.setState({ step: this.state.step + 1 });
    }

    if (!user && !isLoadingUser) {
      history.push('/signups/reviewer');
    }
  }

  forceNextStep = () => {
    const { step } = this.state;
    this.setState({ step: step + 1 });
  };

  forcePreviousStep = () => {
    const { step } = this.state;
    this.setState({ step: step - 1 });
  };

  renderStep = (): JSX.Element => {
    const { user } = this.props;
    const { step } = this.state;
    // this should only be falsey if this user signed up as a customer initially through Oauth
    // in the normal flow this would be set automatically when signing up as a reviewer, or
    // as a customer through the regular sign-up process
    if (!user?.terms_accepted) {
      return <ReviewerProfileTerms />;
    }

    switch (step) {
      case 0:
        return <ReviewerProfileLinks forceNextStep={this.forceNextStep} />;
      case 1:
        return (
          <AdditionalInfo
            previousStep={this.forcePreviousStep}
            forceNextStep={this.forceNextStep}
          />
        );
      case 2:
        return <ReviewerProfileSkills previousStep={this.forcePreviousStep} />;
      case 3:
        return (
          <ReviewerProfileExpertise previousStep={this.forcePreviousStep} />
        );
      default:
        throw new Error(
          'should redirect to /reviewer if step is 4, otherwise an invalid step occurred'
        );
    }
  };

  render(): JSX.Element {
    const { isLoadingUser, user } = this.props;
    const { step } = this.state;
    if (step === 4) {
      return <Redirect to="/reviewer" />;
    }

    return (
      <PageContainer
        mb={6}
        pagetitle="Apply to Join the PullRequest Reviewer Network"
        subtitle={
          <Text variant="body1" mt={3}>
            Join our network of world-class engineers, and help development
            teams ship their best code.
          </Text>
        }
      >
        <Flex justifyContent="stretch" mt={4} height="100%">
          <Div width={320} minWidth={320} mr={4}>
            <SignupStepper
              step={step}
              showTerms={Boolean(user && !user.terms_accepted)}
            />
          </Div>
          {!user || isLoadingUser ? (
            <ContentLoader height="500px" width="100%" />
          ) : (
            this.renderStep()
          )}
        </Flex>
      </PageContainer>
    );
  }
}

function mapStateToProps(state: any) {
  const { signups } = state;
  const account = accountSelector(state);
  const isLoadingUser = isLoadingAccountSelector(state);
  return {
    isUpdating: signups.isUpdating,
    user: account.user,
    isLoadingUser,
    error: signups.error,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({ ...signupsActions, ...accountActions }, dispatch);
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReviewerSignUpProfile);
